import { createContext, type PropsWithChildren, useContext } from 'react';
import { useWindowDimensions } from 'react-native';

export type WidthTrackerProps = PropsWithChildren<{
    /**
     * The amount of width that is now not available.
     */
    width: number;
}>;

/**
 * When rendering HTML via the {@link #HtmlContent} element the content width is required
 * as certain HTML elements need to know the width to render correctly, i.e. li.
 *
 * As we are rendering the page there can be multiple components that could cause horizontal space
 * to be lost and therefore a call to {@link useWindowDimensions} can't be used as-is, and it is
 * difficult to prop drill these widths. This component allows us to keep tracking the width in each
 * component and when we actually render HTML we use this accumulated width to determine the
 * actual available space.
 */
export const WidthTracker = (props: WidthTrackerProps) => {
    const currentWidth = useContext(WidthContext);

    return (
        <WidthContext.Provider value={currentWidth + props.width}>
            {props.children}
        </WidthContext.Provider>
    );
};

/**
 * Determine how much width should be available given the parent components that have been
 * rendered so far.
 */
export const useAvailableWidth = (): number => {
    const { width } = useWindowDimensions();
    const utilisedWidth = useContext(WidthContext);

    return width - utilisedWidth;
};

const WidthContext = createContext(0);
